import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@Components/Button'
import { AccordionSidePane } from '@Components/CMS/AccordionSidePane'
import { SingleProductReview } from '@Components/Product/Reviews/SingleProductReview'
import { Progress } from '@Components/Progress'
import { StarRating } from '@Components/StarRating'
import { Page, PageType, Product, Product_ProductReviewsConnection, useProductReviewsQuery } from '@Generated/graphql'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useMParticle } from '@Globals/MParticle/useMParticle'
import { useTranslation } from '@Globals/Translations'

const AccordionSidePaneHeader = styled.span({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
})

const Headline = styled.h2(() => `
  font: inherit;
  padding: 0 0 5px;
`)

const ContentHeader = styled.span({
  display: 'flex',
  flexDirection: 'column',
  h2: {
    textAlign: 'center',
  },
  justifyContent: 'center',
  marginBottom: 30,
})

const StarRatingWrapper = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  span: {
    display: 'inline-block',
    lineHeight: 1,
    marginLeft: '.6em',
  },
})

const LoaderWrapper = styled.aside`
  display: flex;
  justify-content: center;
  margin: 15px 0;
`

const StyledStarRating = styled(StarRating)({
  fontSize: 15,
})

interface ProductReviewsProps {
  reviews: Product_ProductReviewsConnection
}

export const REVIEW_PAGE_SIZE_ON_PDP = 10

enum ReviewLoaderState {
  IDLE,
  LOADING,
  ERROR,
}

export const ProductReviews: React.VFC<ProductReviewsProps> = ({ reviews }) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const { page: { id = '' } = {} } = useContext(MetadataContext)
  const [currentPage, setCurrentPage] = useState(0)
  const [currentCursor, setCurrentCursor] = useState(reviews.cursor)
  const [reviewsToShow, setReviewsToShow] = useState(reviews.productReviews || [])
  const [localState, setLocalState] = useState(ReviewLoaderState.IDLE)
  const totalPages = useMemo(() => Math.floor(reviews.count / REVIEW_PAGE_SIZE_ON_PDP || 1) - 1, [reviews.count])
  const { otherEvent } = useMParticle()

  // Build a query for the sole purpose of using it lazily later.
  const { fetchMore } = useProductReviewsQuery({
    variables: {
      id,
      type: PageType.Product,
      reviewLimit: REVIEW_PAGE_SIZE_ON_PDP,
      reviewCursor: reviews.cursor,
    },
    skip: true,
  })

  const loadNextPage = useCallback(async () => {
    // If we used "loading" from the query above, it'd only update for the first query and not the subsequent ones.
    // Plus, an enum'ed state is more flexible for the future.
    setLocalState(ReviewLoaderState.LOADING)

    try {
      // Load more items dynamically.
      const { data } = await fetchMore({
        variables: {
          reviewCursor: currentCursor,
        },
      })

      const { page = {} } = data
      const product: Product | null = getProductFieldValue((page as Page)?.fields || [], 'product')

      // Merge the new reviews onto the old ones.
      setReviewsToShow(oldReviews => ([...oldReviews, ...product?.productReviews?.productReviews || []]))

      // Set the local cursor in preparation of loading EVEN MORE DATA!
      setCurrentCursor(product?.productReviews?.cursor || currentCursor)
      setCurrentPage(curPage => curPage + 1)
      setLocalState(ReviewLoaderState.IDLE)

      otherEvent({
        eventName: 'product_reviews_view_more',
        attributes: {},
      })
    } catch {
      setLocalState(ReviewLoaderState.ERROR)
    }
  }, [currentCursor, fetchMore, otherEvent])

  const header = (
    <AccordionSidePaneHeader>
      <span>
        {t('products:PP_REVIEWS_SECTION_TITLE')}
        {' '}
        (
        {reviews.count}
        )
      </span>
      <StyledStarRating rating={reviews.averageRating} />
    </AccordionSidePaneHeader>
  )

  return (
    <AccordionSidePane
      disableResponsive
      fullScreenOnMobile
      header={header}
      onOpen={() => {
        otherEvent({
          eventName: 'product_reviews_open_all',
          attributes: {},
        })
      }}
    >
      <>
        <ContentHeader>
          <Headline>{t('products:PP_REVIEWS_SECTION_TITLE')}</Headline>
          <StarRatingWrapper>
            <StarRating rating={reviews.averageRating} />
            <span>
              (
              {reviews.count}
              )
            </span>
          </StarRatingWrapper>
        </ContentHeader>

        {map(reviewsToShow, review => <SingleProductReview key={review.id} currentLocale={locale} review={review} />)}

        {localState === ReviewLoaderState.LOADING && <LoaderWrapper><Progress /></LoaderWrapper>}

        {currentPage < totalPages && (
          <Button
            disabled={localState === ReviewLoaderState.LOADING}
            onClick={loadNextPage}
          >
            {t('cms:SHOW_MORE_LABEL')}
          </Button>
        )}
      </>
    </AccordionSidePane>
  )
}

